import { Elements } from '@stripe/react-stripe-js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Loader } from '@components/common/Loader';
import {
  tikTokAdvancedMatching,
  tikTokEvent,
  useAnalytics,
} from '@features/Analytics';
import {
  createAppLovinEcommercePayload,
  getAppLovinActualPrice,
} from '@features/Analytics/utils';
import { useFeature, useFeatures } from '@features/FeatureToggle';
import { PAYMENT_MODAL_QUERY_PARAM } from '@features/Paywall';
import {
  useGenerateBraintreeClientToken,
  usePaymentAnalytics,
} from '@features/Paywall/hooks';
import { Disclaimer } from '@features/Paywall/widgets/payment-plans/components/disclaimer';
import { EmptySubscriptionPlanCardList } from '@features/Paywall/widgets/payment-plans/components/empty-subscription-plan-card-list';
import { GuaranteeBlock } from '@features/Paywall/widgets/payment-plans/components/guarantee-block/guarantee-block';
import { MoneyBack } from '@features/Paywall/widgets/payment-plans/components/money-back/money-back';
import { PrivacyAgreement } from '@features/Paywall/widgets/payment-plans/components/privacy-agreement/privacy-agreement';
import {
  CheckoutSelector,
  processingModalStatusAtom,
  useGetStripePromise,
  useGetStripeSubscriptions,
  useModifySubscription,
} from '@features/Stripe';
import {
  errorModalStatusAtom,
  premiumYearAllDataAtom,
  selectedPlanAtom,
  stripePaymentErrorAtom,
  stripeSubscriptionWithPersistence,
  stripeWasPaymentTriedAtom,
  upgradeInfoAtom,
  upsellInfoAtom,
  userAtom,
} from '@features/Stripe/atoms';
import { useGetStripeProductId } from '@features/Stripe/hooks/query/use-get-stripe-product-id';
import { PaymentDeclinedModal } from '@features/Stripe/widgets/payment-declined-modal';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { PaymentModalStatus } from 'src/components/main/payment/payment-modal/payment-modal';
import type {
  TpPaymentPlansStripeProps,
  TpStripeSubscription,
} from 'src/features/Stripe/types';
import { currencyReplacer } from 'src/utils/payment/currency-replacer';
import { BannerType, SimpleBanner } from '../../components/simple-banner';
import { ErrorModalPlans } from './components/error-modal-plans';
import { getPlanRenderProps } from './lib/getPlanRenderProps';

const Combo = dynamic(
  () => import('../subscription-card/combo').then((m) => m.Combo),
  { ssr: false, loading: () => <Loader /> },
);
const Taichi = dynamic(
  () => import('../subscription-card/taichi').then((m) => m.Taichi),
  { ssr: false, loading: () => <Loader /> },
);
const TrialCard = dynamic(
  () => import('../subscription-card/trial-card').then((m) => m.TrialCard),
  { ssr: false, loading: () => <Loader /> },
);
const Challenge = dynamic(
  () => import('../subscription-card/challenge').then((m) => m.Challenge),
  { ssr: false, loading: () => <Loader /> },
);
const SubscriptionPlanCard = dynamic(
  () =>
    import('../subscription-card/subscription-plan-card').then(
      (m) => m.SubscriptionPlanCard,
    ),
  { ssr: false, loading: () => <Loader /> },
);
const NgrowSubscriptionPlanCard = dynamic(
  () =>
    import('../subscription-card/ngrow-subscription-card').then(
      (m) => m.NgrowSubscriptionPlanCard,
    ),
  { ssr: false, loading: () => <Loader /> },
);

const CardWithLink = dynamic(() =>
  import('../subscription-card/stripe-link').then((m) => m.CardWithLink),
);
const SubscriptionPlanCardWithUpgrade = dynamic(() =>
  import('../subscription-card/subscription-plan-card-with-upgrade').then(
    (m) => m.SubscriptionPlanCardWithUpgrade,
  ),
);

import { routes } from '@/constants/navigation';
import { TRIAL_VARIANTS } from '@/constants/variants';
import { CompactDisclaimer } from '@features/Paywall/widgets/payment-plans/components/compact-disclaimer';
import { Printable } from '@features/Paywall/widgets/payment-plans/components/printable';
import styles from './payment-plans-stripe.module.css';

export const PaymentPlans = observer(
  (
    props: TpPaymentPlansStripeProps & {
      shouldSendEvent?: boolean;
      setShowModal?: (
        value: boolean | ((previousState: boolean) => boolean),
      ) => void;
      paymentSectionRef?: any;
      onDiscountTimeFinished?: () => void;
      isInDeclinedFlow?: boolean;
      isBannerWithPromo?: boolean;
      showBanner?: boolean;
    },
  ) => {
    const {
      title,
      inputIdPrefix,
      shouldSendEvent,
      setShowModal,
      paymentSectionRef,
      showBanner = true,
      onDiscountTimeFinished,
      isBannerWithPromo,
      isInDeclinedFlow = false,
    } = props;

    const { mutateAsync: modifySubscription } = useModifySubscription();

    const [subscriptionAgreementChecked, setSubscriptionAgreementChecked] =
      useState(true);

    const [wasNoMoneyPreselected, setWasNoMoneyPreselected] = useState(false);
    const [wasCheckoutShownFromEmail, setWasCheckoutShownFromEmail] =
      useState(false);

    const {
      authStore: { variant, user, access_code: accessCode },
    } = useStores();

    const isTrialVariant = useMemo(
      () => Boolean(variant && TRIAL_VARIANTS.has(variant)),
      [variant],
    );

    const [{ wasPaymentDeclinedOfferAccepted = false }, setPaymentError] =
      useAtom(stripePaymentErrorAtom);

    const isSubscriptionActive = useAtomValue(
      stripeSubscriptionWithPersistence,
    );

    const { wasTried } = useAtomValue(stripeWasPaymentTriedAtom);

    const [planAtom, setPlanAtom] = useAtom(selectedPlanAtom);

    const setUserFields = useSetAtom(userAtom);
    const setUpsellInfoAtom = useSetAtom(upsellInfoAtom);
    const setPremiumYearAllDataAtom = useSetAtom(premiumYearAllDataAtom);
    const setUpgradeInfoAtom = useSetAtom(upgradeInfoAtom);

    useGenerateBraintreeClientToken({
      userFields: { email: user?.email, id: user?.serverId },
      merchantAccountId: planAtom?.braintreeMerchantId,
    });

    const { trackGoogleEvent, trackFacebookEvent, trackAppLovin } =
      useAnalytics();

    const router = useRouter();
    const { query, pathname } = router;

    const {
      payment_intent_client_secret: clientSecretFromUrl,
      redirect_status: redirectStatus,
    } = query;

    const isRedirectStatusFailed =
      !!clientSecretFromUrl && redirectStatus === 'failed';
    const isRedirectStatusSucceeded =
      !!clientSecretFromUrl && redirectStatus === 'succeeded';

    const analyticsCombinedParameters = useMemo(
      () =>
        planAtom.priceId != ''
          ? {
              value: planAtom.fullPriceDiscount
                ? planAtom.fullPriceDiscount.toFixed(2)
                : planAtom.fullPrice.toFixed(2),
              currency: planAtom?.currency,
            }
          : {},
      [planAtom],
    );

    usePaymentAnalytics({
      isRedirectStatusSucceeded,
      isRedirectStatusFailed,
      analyticsCombinedParameters,
      shouldSendEvent,
      setPaymentError,
    });

    const {
      comboPaywall: isComboPaywallEnabled,
      shortLinkPayFeature: isShortLinkPayFeatureEnabled,
      superYouthUpgradePaywall: isSuperYouthUpgradePaywallEnabled,
      taiChiFeature: isTaiChiFeatureEnabled,
      japaneseWalkingFeature: isJwFeatureEnabled,
      japaneseWalking2Feature: isJw2FeatureEnabled,
      introOffer: isIntroOfferEnabled,
    } = useFeatures();

    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');

    const payButtonOuterOfPlans = false;
    // isSWPFeatureEnabled && !isShortLinkPayFeatureEnabled;

    const isNgrowVariant = variant === 'email_ng';

    const { productId } = useGetStripeProductId();

    const { data } = useGetStripeSubscriptions({
      productId: productId,
      wasTried,
    });

    const allSubscriptions = useMemo(() => {
      if (data == null) {
        return [];
      }

      return data.plans;
    }, [data]);

    const noMoneyPlan = useMemo(
      () =>
        allSubscriptions.find((item) => item.noMoneyDiscount) ??
        data?.no_money_plan,
      [allSubscriptions, data?.no_money_plan],
    );

    const visibleSubscriptions = useMemo(
      () => allSubscriptions.filter((item) => !item.noMoneyDiscount),
      [allSubscriptions],
    );

    const initialSubscriptionId = useMemo(() => {
      if (wasPaymentDeclinedOfferAccepted) {
        return noMoneyPlan?.id;
      }

      const subscription =
        visibleSubscriptions.find((item) => item.preselected) ??
        visibleSubscriptions[0];

      return subscription?.id;
    }, [
      noMoneyPlan?.id,
      visibleSubscriptions,
      wasPaymentDeclinedOfferAccepted,
    ]);

    const upgradeInfo = useMemo(() => {
      if (data == null || data.upgrade_plan == null) {
        return {
          currency: 'usd',
          discountPercentage: 0,
          fullPrice: 0,
          fullPriceDiscount: 0,
          upgradeId: '',
          braintreePlanId: '',
        };
      }
      return data.upgrade_plan;
    }, [data]);

    const upsellInfo = useMemo(() => {
      if (data == null || data.upsell_data == null) {
        return {
          currency: 'usd',
          discountPercentage: 0,
          fullPrice: 0,
          fullPriceDiscount: 0,
        };
      }
      return data.upsell_data;
    }, [data]);

    const premiumYearAllDataInfo = useMemo(() => {
      if (
        data == null ||
        data.premium_year_data == null ||
        data.premium_year_discount_data == null
      ) {
        return {
          premiumYearData: {
            currency: 'usd',
            fullPriceDiscount: 0,
            fullPrice: 0,
            discountPercentage: 0,
            braintreePlanId: '',
            upgradeId: '',
          },
          premiumYearDiscountData: {
            currency: 'usd',
            fullPriceDiscount: 0,
            fullPrice: 0,
            discountPercentage: 0,
            braintreePlanId: '',
            upgradeId: '',
          },
        };
      }
      return {
        premiumYearData: data.premium_year_data,
        premiumYearDiscountData: data.premium_year_discount_data,
      };
    }, [data]);

    const oneMonthPlan = useMemo(
      () =>
        allSubscriptions.find(
          (item) =>
            item.billingPeriod === 1 &&
            item.billingPeriodUnit === 'month' &&
            !item.specialOffer &&
            !item.noMoneyDiscount,
        ),
      [allSubscriptions],
    );

    const setPaymentInfoAtoms = useCallback(() => {
      setUpgradeInfoAtom((previous) => ({
        ...previous,
        currency: upgradeInfo.currency,
        discountPercentage: upgradeInfo.discountPercentage,
        fullPrice: upgradeInfo.fullPrice,
        fullPriceDiscount: upgradeInfo.fullPriceDiscount,
        upgradeId: upgradeInfo.upgradeId,
        braintreePlanId: upgradeInfo.braintreePlanId,
      }));
      setPremiumYearAllDataAtom((previous) => ({
        ...previous,
        ...premiumYearAllDataInfo,
      }));
      setUpsellInfoAtom((previous) => ({
        ...previous,
        currency: upsellInfo.currency,
        discountPercentage: upsellInfo.discountPercentage,
        fullPrice: upsellInfo.fullPrice,
        fullPriceDiscount: upsellInfo.fullPriceDiscount,
      }));
    }, [
      setUpgradeInfoAtom,
      setUpsellInfoAtom,
      upgradeInfo.braintreePlanId,
      upgradeInfo.currency,
      upgradeInfo.discountPercentage,
      upgradeInfo.fullPrice,
      upgradeInfo.fullPriceDiscount,
      upgradeInfo.upgradeId,
      upsellInfo.currency,
      upsellInfo.discountPercentage,
      upsellInfo.fullPrice,
      upsellInfo.fullPriceDiscount,
      setPremiumYearAllDataAtom,
      premiumYearAllDataInfo,
    ]);

    const setProcessingModal = useSetAtom(processingModalStatusAtom);
    const setErrorModal = useSetAtom(errorModalStatusAtom);

    const handleSuperYouthUpgradePlan = useCallback(
      ({ id }: { id: string }) => {
        setProcessingModal(true);
        trackGoogleEvent({
          eventName: 'superyouth_upgrade_clicked',
          options: {
            plan_id: id,
            email: user?.email,
          },
        });
        modifySubscription({
          newPlanId: id,
        })
          .then(() => {
            setProcessingModal(false);
            return router.replace({
              pathname: routes.superYouthUpgradeSucceeded,
              query: { ...router.query },
            });
          })
          .catch(() => {
            setProcessingModal(false);
            setErrorModal(true);
          });
      },
      [
        modifySubscription,
        router,
        setProcessingModal,
        user,
        trackGoogleEvent,
        setErrorModal,
      ],
    );

    const openPaymentModalHandler = useCallback(() => {
      if (setShowModal) {
        setShowModal(true);
      }
      router
        .push(
          {
            pathname,
            query: {
              ...query,
              [PAYMENT_MODAL_QUERY_PARAM]: PaymentModalStatus.SHOW,
              ['plan']: planAtom.priceId,
            },
          },
          undefined,
          { shallow: true, scroll: false },
        )
        .then((r) => r);
    }, [pathname, planAtom.priceId, query, router, setShowModal]);

    const scrollPaymentSectionIntoView = useCallback(() => {
      paymentSectionRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, [paymentSectionRef]);

    const closePaymentModalHandler = useCallback(() => {
      if (setShowModal) {
        setShowModal(false);
      }

      router
        .replace(
          {
            pathname: routes.newPlanPaywall,
            query: {
              ...query,
              ['redirect_status']: undefined,
              ['payment_status']: undefined,
            },
          },
          undefined,
          { scroll: false },
        )
        .then((r) => r);

      window.setTimeout(() => scrollPaymentSectionIntoView(), 500);
    }, [query, router, scrollPaymentSectionIntoView, setShowModal]);

    const handlePlanSelect = useCallback(() => {
      openPaymentModalHandler();
      trackGoogleEvent({
        eventName: 'add_to_cart_chosen',
        options: {
          product_id: planAtom.priceId,
          email: user?.email,
        },
      });
    }, [
      openPaymentModalHandler,
      planAtom.priceId,
      trackGoogleEvent,
      user?.email,
    ]);

    const handleSetSelectedPlanId = useCallback(
      (planId: string) => {
        const selectedPlan =
          allSubscriptions.find((plan) => plan.id === planId) ??
          (planId === noMoneyPlan?.id ? noMoneyPlan : undefined);

        // @ts-ignore
        setPlanAtom({
          ...selectedPlan,
          planName: selectedPlan?.name ?? '',
          priceId: selectedPlan?.id ?? '',
          premiumPriceData: selectedPlan?.premiumPriceData,
          premiumDiscountPriceData: selectedPlan?.premiumDiscountPriceData,
        });
      },
      [allSubscriptions, noMoneyPlan, setPlanAtom],
    );

    const selectPlanHandler = useCallback(
      (id: string) => {
        trackGoogleEvent({
          eventName: 'initiate_checkout_another_plan',
        });
        handleSetSelectedPlanId(id);
      },
      [handleSetSelectedPlanId, trackGoogleEvent],
    );

    const renderPlans = useMemo(() => {
      // Case 1: Payment declined offer accepted with noMoneyPlan
      if (wasPaymentDeclinedOfferAccepted && noMoneyPlan)
        return (
          <ErrorModalPlans
            noMoneyPlan={noMoneyPlan as TpStripeSubscription}
            wasTried={wasTried}
            planAtom={planAtom}
            selectPlanHandler={selectPlanHandler}
            handlePlanSelect={handlePlanSelect}
            subscriptionAgreementChecked={subscriptionAgreementChecked}
            inputIdPrefix={inputIdPrefix}
            currencyReplacer={currencyReplacer as any}
          />
        );

      if (visibleSubscriptions.length === 0 && !isNgrowVariant) {
        return <EmptySubscriptionPlanCardList />;
      }

      // Case 2: Ngrow variant
      if (isNgrowVariant) {
        return (
          <NgrowSubscriptionPlanCard
            submitButtonText="Continue"
            key="ngrow-id"
            plan={visibleSubscriptions[0]}
            handlePlanSelect={handlePlanSelect}
          />
        );
      }

      // Case 3: Default mapping of subscriptions
      return visibleSubscriptions.map((item) => {
        const sharedProps = getPlanRenderProps({
          plan: item,
          variant: variant || 'default',
          wasTried,
          isCardSelected: planAtom.priceId === item.id,
          onSelectPlan: selectPlanHandler,
          handlePlanSelect,
          getPlanDisabled:
            !subscriptionAgreementChecked || visibleSubscriptions.length === 0,
          inputIdPrefix,
        });

        if (variant === 'WP20FBShrt_chlg') {
          return <Challenge key={item.id} {...sharedProps} />;
        }

        if (isSuperYouthUpgradePaywallEnabled) {
          return (
            <SubscriptionPlanCardWithUpgrade
              {...sharedProps}
              key={item.id}
              submitButtonText="Get Plan"
              handlePlanSelect={() =>
                handleSuperYouthUpgradePlan({ id: item.id })
              }
            />
          );
        }
        if (isTrialVariant) {
          return <TrialCard key={item.id} {...sharedProps} />;
        }
        if (isShortLinkPayFeatureEnabled)
          return <CardWithLink key={item.id} {...sharedProps} />;
        if (
          isTaiChiFeatureEnabled ||
          isJwFeatureEnabled ||
          isJw2FeatureEnabled
        ) {
          return <Taichi key={item.id} {...sharedProps} />;
        }
        if (isComboPaywallEnabled)
          return (
            <Combo
              key={item.id}
              {...sharedProps}
              showUsersCompareBadge={!isTaiChiFeatureEnabled}
            />
          );
        return (
          <SubscriptionPlanCard
            key={item.id}
            submitButtonText="GET MY PLAN"
            {...sharedProps}
          />
        );
      });
    }, [
      isShortLinkPayFeatureEnabled,
      isTaiChiFeatureEnabled,
      isTrialVariant,
      visibleSubscriptions,
      isNgrowVariant,
      wasPaymentDeclinedOfferAccepted,
      noMoneyPlan,
      isComboPaywallEnabled,
      planAtom,
      isJwFeatureEnabled,
      isJw2FeatureEnabled,
      planAtom.priceId,
      wasTried,
      variant,
      subscriptionAgreementChecked,
      handlePlanSelect,
      selectPlanHandler,
      inputIdPrefix,
      isSuperYouthUpgradePaywallEnabled,
      handleSuperYouthUpgradePlan,
    ]);

    const renderFooterContent = useCallback(() => {
      if (isNgrowVariant || payButtonOuterOfPlans) return null;

      const agreement = (
        <>
          <PrivacyAgreement
            subscriptionAgreementChecked={subscriptionAgreementChecked!}
            setSubscriptionAgreementChecked={setSubscriptionAgreementChecked}
          />

          <GuaranteeBlock />
        </>
      );

      if (isTrialVariant) {
        return (
          <>
            <Printable />
            <MoneyBack largerVerticle={isComboPaywallEnabled} />

            <p className={styles.disclaimer}>
              After {planAtom.introOfferData.billingPeriod}{' '}
              {planAtom.introOfferData.billingPeriodUnit}, auto-renews at{' '}
              {currencyReplacer(planAtom.currency)}
              {planAtom.fullPriceDiscount.toFixed(2)}. billed every{' '}
              {planAtom.billingPeriod} {planAtom.billingPeriodUnit} and includes
              unlimited access to Youth content, readings and tests. Cancel
              anytime.
            </p>
          </>
        );
      }

      if (isSuperYouthUpgradePaywallEnabled) {
        return agreement;
      }

      return (
        <>
          {isJw2FeatureEnabled && <Printable />}
          <MoneyBack largerVerticle={isComboPaywallEnabled} />

          {isComboPaywallEnabled &&
          !isInDeclinedFlow &&
          !isShortLinkPayFeatureEnabled ? (
            <Disclaimer />
          ) : (
            agreement
          )}
        </>
      );
    }, [
      planAtom,
      isNgrowVariant,
      isComboPaywallEnabled,
      isInDeclinedFlow,
      isTrialVariant,
      isShortLinkPayFeatureEnabled,
      isSuperYouthUpgradePaywallEnabled,
      subscriptionAgreementChecked,
      isJw2FeatureEnabled,
    ]);

    useEffect(() => {
      if (allSubscriptions.length > 0) {
        trackGoogleEvent({
          eventName: 'paywall_plans_loaded',
          options: {
            plans: allSubscriptions.map((plan) => {
              return { planId: plan.id, btPlanId: plan.braintreePlanId };
            }),
          },
        });
      }
    }, [allSubscriptions, trackGoogleEvent]);

    useEffect(() => {
      setPaymentInfoAtoms();
    }, [setPaymentInfoAtoms]);

    useEffect(() => {
      if (shouldSendEvent) {
        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'AddToCart',
            options: {
              email: user?.email,
            },
          });
        trackGoogleEvent({
          eventName: 'add_to_cart_open',
          options: {
            email: user?.email,
          },
        });
        user?.email ? tikTokAdvancedMatching(user.email) : null;
        tikTokEvent('AddToCart');

        setUserFields((previous) => ({
          ...previous,
          email: user?.email ?? previous.email,
          token: user?.token ?? previous.token,
          userId: user?.serverId ?? previous.userId,
          accessCode: accessCode ?? previous.accessCode,
        }));
      }
    }, [
      setUserFields,
      shouldSendEvent,
      fbPixelSept2025Feature,
      trackFacebookEvent,
      trackGoogleEvent,
      accessCode,
      user,
    ]);

    useEffect(() => {
      if (!shouldSendEvent || !planAtom.priceId) {
        return;
      }

      const appLovinActualPrice = getAppLovinActualPrice({
        fullPrice: planAtom.fullPrice,
        fullPriceDiscount: planAtom.fullPriceDiscount,
        introOfferData: planAtom.introOfferData,
        isIntroOfferEnabled: isIntroOfferEnabled || variant === 'email_ng',
      });
      const appLovinCurrency =
        planAtom.introOfferData?.currency || planAtom.currency;
      const appLovinPlanPayload = createAppLovinEcommercePayload({
        currency: appLovinCurrency,
        value: appLovinActualPrice,
        itemId: planAtom.priceId,
        itemName: planAtom.planName || planAtom.name,
        userEmail: user?.email,
        userId: user?.serverId,
        variant,
      });
      const appLovinLeadPayload = createAppLovinEcommercePayload({
        currency: appLovinCurrency,
        value: appLovinActualPrice,
        userEmail: user?.email,
        userId: user?.serverId,
      });

      trackAppLovin({
        eventName: 'view_item',
        options: appLovinPlanPayload,
      });
      trackAppLovin({
        eventName: 'generate_lead',
        options: appLovinLeadPayload,
      });
      trackAppLovin({
        eventName: 'add_to_cart',
        options: appLovinPlanPayload,
      });
    }, [
      shouldSendEvent,
      planAtom.priceId,
      planAtom.fullPrice,
      planAtom.fullPriceDiscount,
      planAtom.introOfferData,
      planAtom.currency,
      planAtom.planName,
      planAtom.name,
      trackAppLovin,
      user?.email,
      user?.serverId,
      variant,
      isIntroOfferEnabled,
    ]);

    useEffect(() => {
      if (planAtom.priceId != '' && !planAtom.specialOffer && wasTried) {
        handleSetSelectedPlanId(
          allSubscriptions.filter(
            (item) => item.name === planAtom.planName && item.specialOffer,
          )[0]?.id,
        );
      }
      if (initialSubscriptionId && !planAtom.priceId) {
        handleSetSelectedPlanId(initialSubscriptionId);
      }
      if (
        wasPaymentDeclinedOfferAccepted &&
        noMoneyPlan &&
        oneMonthPlan &&
        !wasNoMoneyPreselected
      ) {
        setWasNoMoneyPreselected(true);
        handleSetSelectedPlanId(noMoneyPlan.id);
      }
    }, [
      allSubscriptions,
      noMoneyPlan,
      oneMonthPlan,
      handleSetSelectedPlanId,
      wasPaymentDeclinedOfferAccepted,
      wasTried,
      wasNoMoneyPreselected,
      planAtom.planName,
      planAtom.priceId,
      planAtom.specialOffer,
      initialSubscriptionId,
    ]);

    useEffect(() => {
      if (
        !wasCheckoutShownFromEmail &&
        (!user?.isSubscriptionActive ||
          isSubscriptionActive !== 'subscriptionActive') &&
        allSubscriptions.length > 0 &&
        variant === 'variantemail4' &&
        !wasPaymentDeclinedOfferAccepted
      ) {
        setWasCheckoutShownFromEmail(true);
        handlePlanSelect();
      }
    }, [
      allSubscriptions,
      handlePlanSelect,
      isSubscriptionActive,
      user?.isSubscriptionActive,
      variant,
      wasCheckoutShownFromEmail,
      wasPaymentDeclinedOfferAccepted,
    ]);

    const stripePromise = useGetStripePromise();

    return (
      <>
        <div ref={paymentSectionRef} className={styles.container}>
          {title && <h3 className={styles.titleSection}>{title}</h3>}

          {showBanner &&
            isComboPaywallEnabled &&
            !wasPaymentDeclinedOfferAccepted && (
              <SimpleBanner
                className={styles.banner}
                variant={isBannerWithPromo ? BannerType.PROMO : BannerType.RED}
                onFinish={onDiscountTimeFinished}
              />
            )}

          <ul className={styles.plansContainer}>{renderPlans}</ul>

          {renderFooterContent()}

          {payButtonOuterOfPlans && (
            <>
              <Elements stripe={stripePromise}>
                <CheckoutSelector
                  isCompactStyle
                  isOnlyPaymentRequestButton
                  priorityMethod="Apple Pay"
                />
              </Elements>
              <CompactDisclaimer
                price={`${currencyReplacer(planAtom.currency)}${planAtom.fullPriceDiscount}`}
                billingPeriod={planAtom.billingPeriod}
                billingPeriodUnit={planAtom.billingPeriodUnit}
              />
            </>
          )}

          {noMoneyPlan && shouldSendEvent && (
            <PaymentDeclinedModal onClose={closePaymentModalHandler} />
          )}
        </div>
      </>
    );
  },
);

PaymentPlans.displayName = 'PaymentPlans';
